export interface LayoutSnapshotSyncOptions {
  copyGeometry?: boolean;
}

export interface LayoutSnapshotValidationOptions {
  compareGeometry?: boolean;
}

export interface LayoutSnapshotSyncStats {
  graphicPieces: number;
  cutPieces: number;
  matched: number;
  updated: number;
  unmatchedGraphicPieces: string[];
  unmatchedCutPieces: string[];
}

export interface LayoutSnapshotSyncIssue {
  identity: string;
  field: string;
  graphicValue: any;
  cutValue: any;
}

export interface LegacyStructuralStripNormalizationStats {
  inspectedSheets: number;
  removedStrips: number;
  resizedStrips: number;
  affectedStrips: string[];
}

const ASSIGNMENT_FIELDS = [
  'easel',
  'easel_id',
  'easel_num',
  'group',
  'side',
  'stack',
  'stack_num',
  'cut_order',
  'sequence_broked'
];

const GEOMETRY_FIELDS = [
  'width',
  'height',
  'rotation',
  'origin_x',
  'origin_y'
];

function hasValue(valueIn: any): boolean {
  return valueIn !== undefined && valueIn !== null;
}

function cloneValue(valueIn: any): any {
  if (!hasValue(valueIn)) {
    return valueIn;
  }
  if (typeof valueIn !== 'object') {
    return valueIn;
  }
  return JSON.parse(JSON.stringify(valueIn));
}

function getPieceType(itemIn: any): string {
  if (!itemIn) {
    return '';
  }
  if (hasValue(itemIn.type)) {
    return itemIn.type.toString();
  }
  if (itemIn.draw && hasValue(itemIn.draw.type)) {
    return itemIn.draw.type.toString();
  }
  return '';
}

function getIdentityValue(itemIn: any, fieldIn: string): any {
  if (!itemIn) {
    return null;
  }
  if (hasValue(itemIn[fieldIn])) {
    return itemIn[fieldIn];
  }
  if (itemIn.draw && hasValue(itemIn.draw[fieldIn])) {
    return itemIn.draw[fieldIn];
  }
  return null;
}

function normalizedText(valueIn: any): string {
  return hasValue(valueIn) ? valueIn.toString() : '';
}

function normalizedNumber(valueIn: any): string {
  const value = Number(valueIn);
  return Number.isFinite(value) ? value.toFixed(3) : '';
}

function buildIdentityCandidates(itemIn: any): string[] {
  const nameId = normalizedText(getIdentityValue(itemIn, 'name_id'));
  const name = normalizedText(getIdentityValue(itemIn, 'name'));
  const num = normalizedText(getIdentityValue(itemIn, 'num'));
  const lot = normalizedText(getIdentityValue(itemIn, 'lot'));
  const sequence = normalizedText(getIdentityValue(itemIn, 'sequence'));
  const sequenceUnit = normalizedText(getIdentityValue(itemIn, 'sequence_unit'));
  const orderno = normalizedText(getIdentityValue(itemIn, 'orderno'));
  const orderpos = normalizedText(getIdentityValue(itemIn, 'orderpos'));
  const width = normalizedNumber(getIdentityValue(itemIn, 'width'));
  const height = normalizedNumber(getIdentityValue(itemIn, 'height'));
  const candidates = [];

  if (nameId && num) {
    candidates.push('name-id-num|' + nameId + '|' + num);
  }
  if (nameId) {
    candidates.push('name-id|' + nameId);
  }
  if (name && num) {
    candidates.push('name-num|' + name + '|' + num);
  }
  if (lot && sequence && sequenceUnit) {
    candidates.push([
      'production-unit',
      lot,
      sequence,
      sequenceUnit,
      orderno,
      orderpos,
      width,
      height
    ].join('|'));
  }

  return candidates;
}

function describeIdentity(itemIn: any): string {
  const candidates = buildIdentityCandidates(itemIn);
  if (candidates.length > 0) {
    return candidates[0];
  }
  return 'unidentified-piece';
}

function flattenGraphicPieces(graphicListIn: any[]): any[] {
  const result = [];
  (Array.isArray(graphicListIn) ? graphicListIn : []).forEach(group => {
    (Array.isArray(group) ? group : []).forEach(item => {
      if (item && getPieceType(item) === 'P') {
        result.push(item);
      }
    });
  });
  return result;
}

function getCutPieces(cutOrderListIn: any[]): any[] {
  return (Array.isArray(cutOrderListIn) ? cutOrderListIn : [])
    .filter(item => item && getPieceType(item) === 'P');
}

function buildCutCandidateIndex(cutPiecesIn: any[]): any {
  const index = {};
  cutPiecesIn.forEach((piece, pieceIndex) => {
    buildIdentityCandidates(piece).forEach(candidate => {
      if (!index[candidate]) {
        index[candidate] = [];
      }
      index[candidate].push(pieceIndex);
    });
  });
  return index;
}

function findMatchingCutIndex(graphicPieceIn: any, candidateIndexIn: any, usedCutIndexesIn: boolean[]): number {
  const candidates = buildIdentityCandidates(graphicPieceIn);
  for (let candidateIndex = 0; candidateIndex < candidates.length; candidateIndex++) {
    const indexed = candidateIndexIn[candidates[candidateIndex]] || [];
    for (let indexedPos = 0; indexedPos < indexed.length; indexedPos++) {
      const cutIndex = indexed[indexedPos];
      if (!usedCutIndexesIn[cutIndex]) {
        return cutIndex;
      }
    }
  }
  return -1;
}

function valuesEqual(valueAIn: any, valueBIn: any): boolean {
  if (!hasValue(valueAIn) && !hasValue(valueBIn)) {
    return true;
  }
  if (typeof valueAIn === 'number' || typeof valueBIn === 'number') {
    const a = Number(valueAIn);
    const b = Number(valueBIn);
    if (Number.isFinite(a) && Number.isFinite(b)) {
      return Math.abs(a - b) <= 0.001;
    }
  }
  return normalizedText(valueAIn) === normalizedText(valueBIn);
}

function pointSignature(pointsIn: any): string {
  if (!Array.isArray(pointsIn)) {
    return '';
  }
  return pointsIn.map(point => [
    normalizedNumber(point && point.x),
    normalizedNumber(point && point.y)
  ].join(',')).join(';');
}

function copyField(targetIn: any, sourceIn: any, fieldIn: string): boolean {
  if (!hasValue(sourceIn && sourceIn[fieldIn])) {
    return false;
  }
  if (valuesEqual(targetIn && targetIn[fieldIn], sourceIn[fieldIn])) {
    return false;
  }
  targetIn[fieldIn] = cloneValue(sourceIn[fieldIn]);
  return true;
}

function finiteNumber(valueIn: any, fallbackIn: number = 0): number {
  const value = Number(valueIn);
  return Number.isFinite(value) ? value : fallbackIn;
}

function rectanglePoints(xIn: number, yIn: number, widthIn: number, heightIn: number): any[] {
  return [
    { x: xIn, y: yIn },
    { x: xIn, y: yIn + heightIn },
    { x: xIn + widthIn, y: yIn + heightIn },
    { x: xIn + widthIn, y: yIn }
  ];
}

function rectanglesOverlap(rectAIn: any, rectBIn: any, epsilonIn: number): boolean {
  return rectAIn.x < (rectBIn.x + rectBIn.width - epsilonIn) &&
    (rectAIn.x + rectAIn.width) > (rectBIn.x + epsilonIn) &&
    rectAIn.y < (rectBIn.y + rectBIn.height - epsilonIn) &&
    (rectAIn.y + rectAIn.height) > (rectBIn.y + epsilonIn);
}

function updateLegacyStripGeometry(traverseIn: any, widthIn: number, heightIn: number): void {
  const x = finiteNumber(traverseIn.origin_x);
  const y = 0;
  const area = Number(((widthIn * heightIn) / 1000000).toFixed(2));
  const previousArea = finiteNumber(traverseIn.area, 0);
  const previousWeight = finiteNumber(traverseIn.weight, 0);

  traverseIn.origin_y = y;
  traverseIn.width = widthIn;
  traverseIn.height = heightIn;
  traverseIn.area = area;
  traverseIn.points = rectanglePoints(x, y, widthIn, heightIn);

  if (traverseIn.draw && typeof traverseIn.draw === 'object') {
    const draw = traverseIn.draw;
    draw.width = widthIn;
    draw.height = heightIn;
    draw.area = area;
    draw.label = Math.round(widthIn) + 'x' + Math.round(heightIn);
    draw.points = rectanglePoints(x, y, widthIn, heightIn);
    if (previousArea > 0 && previousWeight > 0) {
      draw.weight = Number((previousWeight * (area / previousArea)).toFixed(2));
    }
  }
}

// Los snapshots v1 podían materializar una segunda franja de cierre a la
// derecha usando las dimensiones brutas de la hoja. Esa franja se solapaba con
// el retal real y, al cambiar el origen, sobrepasaba los desbandes superior e
// inferior. Se elimina si ya hay geometría real en ese espacio y sólo se
// redimensiona cuando es la única representación disponible.
export function normalizeLegacyStructuralRightStrips(
  sheetListIn: any[]
): LegacyStructuralStripNormalizationStats {
  const stats: LegacyStructuralStripNormalizationStats = {
    inspectedSheets: 0,
    removedStrips: 0,
    resizedStrips: 0,
    affectedStrips: []
  };
  const epsilon = 0.001;

  (Array.isArray(sheetListIn) ? sheetListIn : []).forEach((sheet, sheetIndex) => {
    if (!sheet || !Array.isArray(sheet.traverses)) {
      return;
    }
    stats.inspectedSheets++;

    const sheetWidth = finiteNumber(sheet.width);
    const sheetHeight = finiteNumber(sheet.height);
    const disband = sheet.disband || {};
    const left = Math.max(0, finiteNumber(disband.left));
    const right = Math.max(0, finiteNumber(disband.right));
    const top = Math.max(0, finiteNumber(disband.top));
    const bottom = Math.max(0, finiteNumber(disband.bottom));
    const usableWidth = sheetWidth - left - right;
    const usableHeight = sheetHeight - top - bottom;
    if (!(usableWidth > epsilon && usableHeight > epsilon)) {
      return;
    }

    const pieceRightEdges = (Array.isArray(sheet.pieces) ? sheet.pieces : [])
      .map(wrapper => {
        const x = finiteNumber(wrapper && wrapper.origin_x, NaN);
        const width = finiteNumber(wrapper && wrapper.piece && wrapper.piece.piece_width, NaN);
        return Number.isFinite(x) && Number.isFinite(width) ? x + width : NaN;
      })
      .filter(value => Number.isFinite(value));
    if (pieceRightEdges.length < 1) {
      return;
    }
    const xCut = Math.max.apply(null, pieceRightEdges);
    const expectedWidth = usableWidth - xCut;
    if (!(xCut > epsilon && expectedWidth > epsilon)) {
      return;
    }

    const expectedRect = {
      x: xCut,
      y: 0,
      width: expectedWidth,
      height: usableHeight
    };
    const candidates = sheet.traverses.filter(traverse => {
      if (!traverse || traverse.piece || traverse.used) {
        return false;
      }
      const x = finiteNumber(traverse.origin_x, NaN);
      const y = finiteNumber(traverse.origin_y, NaN);
      const width = finiteNumber(traverse.width, NaN);
      const height = finiteNumber(traverse.height, NaN);
      if (![x, y, width, height].every(value => Number.isFinite(value))) {
        return false;
      }

      const isExpectedCut = Math.abs(x - xCut) <= epsilon;
      const usesRawRightEdge = Math.abs((x + width) - sheetWidth) <= epsilon;
      const usesRawHeight = Math.abs(y) <= epsilon && Math.abs(height - sheetHeight) <= epsilon;
      const isMarkedStructural = sheet.remainder_structural_strip_materialized === true ||
        traverse.fixed_cut === true;
      return isMarkedStructural && isExpectedCut && usesRawRightEdge && usesRawHeight;
    });

    if (candidates.length < 1) {
      return;
    }

    const candidateSet = new Set(candidates);
    const occupiedByRealTraverse = sheet.traverses.some(traverse => {
      if (!traverse || candidateSet.has(traverse) || traverse.piece || traverse.used) {
        return false;
      }
      const rect = {
        x: finiteNumber(traverse.origin_x, NaN),
        y: finiteNumber(traverse.origin_y, NaN),
        width: finiteNumber(traverse.width, NaN),
        height: finiteNumber(traverse.height, NaN)
      };
      if (![rect.x, rect.y, rect.width, rect.height].every(value => Number.isFinite(value))) {
        return false;
      }
      return rectanglesOverlap(rect, expectedRect, epsilon);
    });

    if (occupiedByRealTraverse) {
      sheet.traverses = sheet.traverses.filter(traverse => !candidateSet.has(traverse));
      candidates.forEach(traverse => {
        stats.removedStrips++;
        stats.affectedStrips.push(
          traverse.name ? traverse.name.toString() : 'sheet-' + (sheetIndex + 1)
        );
      });
      sheet.remainder_structural_strip_materialized = false;
      return;
    }

    candidates.forEach(traverse => {
      updateLegacyStripGeometry(traverse, expectedWidth, usableHeight);
      stats.resizedStrips++;
      stats.affectedStrips.push(
        traverse.name ? traverse.name.toString() : 'sheet-' + (sheetIndex + 1)
      );
    });
    sheet.remainder_structural_strip_materialized = true;
  });

  return stats;
}

export function synchronizeSnapshotGraphicsWithCutOrder(
  graphicListIn: any[],
  cutOrderListIn: any[],
  optionsIn?: LayoutSnapshotSyncOptions
): LayoutSnapshotSyncStats {
  const graphicPieces = flattenGraphicPieces(graphicListIn);
  const cutPieces = getCutPieces(cutOrderListIn);
  const candidateIndex = buildCutCandidateIndex(cutPieces);
  const usedCutIndexes = cutPieces.map(() => false);
  const unmatchedGraphicPieces = [];
  let matched = 0;
  let updated = 0;

  graphicPieces.forEach(graphicPiece => {
    const cutIndex = findMatchingCutIndex(graphicPiece, candidateIndex, usedCutIndexes);
    if (cutIndex < 0) {
      unmatchedGraphicPieces.push(describeIdentity(graphicPiece));
      return;
    }

    const cutPiece = cutPieces[cutIndex];
    usedCutIndexes[cutIndex] = true;
    matched++;
    let pieceUpdated = false;

    ASSIGNMENT_FIELDS.forEach(field => {
      if (copyField(graphicPiece, cutPiece, field)) {
        pieceUpdated = true;
      }
    });

    if (optionsIn && optionsIn.copyGeometry === true) {
      GEOMETRY_FIELDS.forEach(field => {
        if (copyField(graphicPiece, cutPiece, field)) {
          pieceUpdated = true;
        }
      });
      if (Array.isArray(cutPiece.points) &&
          pointSignature(graphicPiece.points) !== pointSignature(cutPiece.points)) {
        graphicPiece.points = cloneValue(cutPiece.points);
        pieceUpdated = true;
      }
    }

    if (pieceUpdated) {
      updated++;
    }
  });

  const unmatchedCutPieces = cutPieces
    .filter((piece, index) => !usedCutIndexes[index])
    .map(piece => describeIdentity(piece));

  return {
    graphicPieces: graphicPieces.length,
    cutPieces: cutPieces.length,
    matched,
    updated,
    unmatchedGraphicPieces,
    unmatchedCutPieces
  };
}

export function validateSnapshotGraphicsAgainstCutOrder(
  graphicListIn: any[],
  cutOrderListIn: any[],
  optionsIn?: LayoutSnapshotValidationOptions
): LayoutSnapshotSyncIssue[] {
  const graphicPieces = flattenGraphicPieces(graphicListIn);
  const cutPieces = getCutPieces(cutOrderListIn);
  const candidateIndex = buildCutCandidateIndex(cutPieces);
  const usedCutIndexes = cutPieces.map(() => false);
  const issues = [];

  graphicPieces.forEach(graphicPiece => {
    const cutIndex = findMatchingCutIndex(graphicPiece, candidateIndex, usedCutIndexes);
    if (cutIndex < 0) {
      issues.push({
        identity: describeIdentity(graphicPiece),
        field: 'identity',
        graphicValue: 'present',
        cutValue: 'missing'
      });
      return;
    }

    const cutPiece = cutPieces[cutIndex];
    const identity = describeIdentity(cutPiece);
    usedCutIndexes[cutIndex] = true;

    const fieldsToCompare = (optionsIn && optionsIn.compareGeometry === false)
      ? ASSIGNMENT_FIELDS
      : ASSIGNMENT_FIELDS.concat(GEOMETRY_FIELDS);
    fieldsToCompare.forEach(field => {
      if (hasValue(cutPiece[field]) && !valuesEqual(graphicPiece[field], cutPiece[field])) {
        issues.push({
          identity,
          field,
          graphicValue: graphicPiece[field],
          cutValue: cutPiece[field]
        });
      }
    });

    if ((!optionsIn || optionsIn.compareGeometry !== false) &&
        Array.isArray(cutPiece.points) &&
        pointSignature(graphicPiece.points) !== pointSignature(cutPiece.points)) {
      issues.push({
        identity,
        field: 'points',
        graphicValue: pointSignature(graphicPiece.points),
        cutValue: pointSignature(cutPiece.points)
      });
    }
  });

  cutPieces.forEach((cutPiece, index) => {
    if (!usedCutIndexes[index]) {
      issues.push({
        identity: describeIdentity(cutPiece),
        field: 'identity',
        graphicValue: 'missing',
        cutValue: 'present'
      });
    }
  });

  return issues;
}
